import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/auth";
import { PageHeader, Card, Btn, Icon } from "@/components/kit";
import { toast } from "sonner";

const STAGES = ["New", "Contacted", "Qualified", "Meeting", "Proposal", "Converted", "Lost"];
// map legacy CRM stages -> lead stages
const STAGE_MAP = { Lead: "New", Contacted: "Contacted", Meeting: "Meeting", Proposal: "Proposal", Invested: "Converted", "Active Client": "Converted" };
const SOURCES = ["Website", "WhatsApp", "Social Media", "Campaigns", "Referrals", "Events", "Manual Entry", "Podcast/Video", "Landing Pages"];
const band = (s) => (s >= 80 ? { t: "Hot", c: "#EF4444" } : s >= 60 ? { t: "Warm", c: "#F59E0B" } : { t: "Cold", c: "#3B82F6" });

export default function Leads() {
  const nav = useNavigate();
  const [leads, setLeads] = useState([]);
  const [filter, setFilter] = useState("All");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name: "", source: "Referrals", location: "", score: 60, stage: "New", note: "" });

  const load = useCallback(() => api.get("/crm/leads").then((r) => setLeads(r.data.map((l) => ({ ...l, stage: STAGE_MAP[l.stage] || l.stage })))).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const view = useMemo(() => {
    let v = [...leads].sort((a, b) => b.score - a.score);
    if (filter === "Hot") v = v.filter((l) => l.score >= 80);
    else if (filter === "Warm") v = v.filter((l) => l.score >= 60 && l.score < 80);
    else if (filter === "Cold") v = v.filter((l) => l.score < 60);
    return v;
  }, [leads, filter]);
  const callToday = useMemo(() => [...leads].filter((l) => l.score >= 80 && !["Converted", "Lost"].includes(l.stage)).sort((a, b) => b.score - a.score).slice(0, 5), [leads]);

  const setStage = async (l, stage) => { setLeads((ls) => ls.map((x) => (x.id === l.id ? { ...x, stage } : x))); await api.patch(`/crm/leads/${l.id}`, { stage: stage === "New" ? "Lead" : stage }); };
  const add = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    const { data } = await api.post("/crm/leads", { ...form, stage: "Lead", score: Number(form.score) });
    setLeads((ls) => [{ ...data, stage: "New" }, ...ls]); setAdding(false);
    setForm({ name: "", source: "Referrals", location: "", score: 60, stage: "New", note: "" }); toast.success("Lead added");
  };

  return (
    <div className="fade-up">
      <PageHeader demo={false} title="Leads" subtitle="Capture → qualify → convert. AI scores and prioritises your pipeline." icon="Target" color="#F97316"
        action={<Btn onClick={() => setAdding(true)} data-testid="lead-add-btn"><Icon name="Plus" size={16} /> Add Lead</Btn>} />

      {/* Leads to call today */}
      <Card className="mb-6">
        <div className="flex items-center gap-2 mb-3"><Icon name="PhoneCall" size={18} className="text-orange-500" /><h3 className="font-head font-semibold text-lg">Leads you should call today</h3></div>
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="call-today">
          {callToday.length ? callToday.map((l) => (
            <div key={l.id} className="flex items-center gap-3 rounded-xl border border-orange-100 bg-orange-50/50 px-3 py-2.5">
              <div className="min-w-0"><div className="font-semibold text-sm truncate">{l.name}</div><div className="text-xs text-gray-500 truncate">{l.note || l.source}</div></div>
              <span className="ml-auto text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: `${band(l.score).c}22`, color: band(l.score).c }}>{band(l.score).t} · {l.score}</span>
            </div>
          )) : <p className="text-sm text-gray-400">No hot leads right now.</p>}
        </div>
      </Card>

      <div className="flex gap-2 mb-4 overflow-x-auto no-scrollbar">
        {["All", "Hot", "Warm", "Cold"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`lead-filter-${f.toLowerCase()}`}
            className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap border ${filter === f ? "grad-blue-violet text-white border-transparent" : "bg-white border-gray-200 text-gray-600"}`}>{f}</button>
        ))}
      </div>

      {adding && (
        <Card className="mb-5">
          <div className="grid sm:grid-cols-3 gap-3">
            <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name-input" className={inp} />
            <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className={inp} />
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={inp}>{SOURCES.map((s) => <option key={s}>{s}</option>)}</select>
            <input placeholder="Lead score (0-100)" type="number" value={form.score} onChange={(e) => setForm({ ...form, score: e.target.value })} className={inp} />
            <input placeholder="Next action / note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={`${inp} sm:col-span-2`} />
          </div>
          <div className="flex gap-2 mt-3"><Btn onClick={add} data-testid="lead-save-btn">Save Lead</Btn><Btn variant="outline" onClick={() => setAdding(false)}>Cancel</Btn></div>
        </Card>
      )}

      <div className="space-y-2" data-testid="leads-list">
        {view.map((l) => (
          <div key={l.id} className="card-mfx p-4 flex flex-wrap items-center gap-3" data-testid="lead-row">
            <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: band(l.score).c }} />
            <div className="min-w-0"><div className="font-semibold text-sm">{l.name}</div><div className="text-xs text-gray-400 flex items-center gap-2"><Icon name="MapPin" size={11} />{l.location || "—"} · {l.source}</div></div>
            <span className="text-[10px] font-bold rounded-full px-2 py-0.5" style={{ background: `${band(l.score).c}22`, color: band(l.score).c }}>{band(l.score).t} {l.score}</span>
            {l.note && <span className="text-xs text-gray-500 hidden md:block truncate max-w-[220px]">{l.note}</span>}
            <div className="ml-auto flex items-center gap-2">
              <select value={l.stage} onChange={(e) => setStage(l, e.target.value)} data-testid={`lead-stage-${l.id}`} className="text-xs rounded-lg border border-gray-200 px-2 py-1.5 outline-none">
                {STAGES.map((s) => <option key={s}>{s}</option>)}
              </select>
              <button onClick={() => nav("/app/ai", { state: { prompt: `How should I approach and convert this lead: ${l.name} from ${l.source}, score ${l.score}. Notes: ${l.note || "none"}. Give me a short outreach plan and a WhatsApp opener.` } })} title="Ask AI" className="text-violet-500 hover:text-violet-700 p-1" data-testid={`lead-ask-ai-${l.id}`}><Icon name="Sparkles" size={16} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
const inp = "w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm outline-none focus:border-blue-400";
